import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AiDraft from "@/components/partner/AiDraft";
import ItineraryTimeline from "@/components/ItineraryTimeline";
import { errMsg } from "@/lib/api";

const TYPES = ["travel", "meal", "session", "free", "stay"];
const blank = (day) => ({ day, start_time: "09:00", end_time: "", block_type: "session", title: "", location: "", curator: "", description: "" });

function parseDraft(text) {
  return text.split("\n").map((ln) => ln.split("|").map((x) => x.trim())).filter((p) => p.length >= 5 && /day\s*\d+/i.test(p[0]))
    .map(([d, s, e, t, title, loc]) => ({ day: Number(d.replace(/\D/g, "")), start_time: s, end_time: e, block_type: TYPES.includes(t) ? t : "session", title, location: loc || "", curator: "", description: "" }));
}

export default function ItineraryTab({ data, api, reload }) {
  const l = data.listing;
  const [blocks, setBlocks] = useState(data.itinerary.map(({ id, listing_id, ...b }) => b));
  const [summary, setSummary] = useState("");
  const pending = data.change_requests.find((c) => c.status === "awaiting_consent");
  const upd = (i, k, v) => setBlocks(blocks.map((b, j) => (j === i ? { ...b, [k]: k === "day" ? Number(v) : v } : b)));

  const save = async () => {
    try {
      const { data: r } = await api.put(`/partner/listings/${l.id}/itinerary`, { blocks: blocks.filter((b) => b.title.trim()), change_summary: summary });
      toast.success(r.consent_required ? `Material change sent to ${r.affected} booking(s) for consent` : `Saved ${r.saved} blocks`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-10 xl:grid-cols-[1.3fr_1fr]" data-testid="itinerary-tab">
      <div className="space-y-4">
        {pending && <div className="rounded-lg border border-secondary/40 bg-secondary/5 p-4 text-sm" data-testid="pending-change-note">A material change is awaiting guest consent: “{pending.summary}”. {Object.keys(pending.responses || {}).length}/{pending.affected_booking_ids.length} responded.</div>}
        <AiDraft kind="itinerary" applyLabel="Replace blocks with this draft" context={() => `${l.title} in ${l.destination}, ${l.days} days. Vibe ${l.vibe}. Curators: ${(l.host_stack?.curators || []).map((c) => c.name).join(", ")}`}
          onApply={(t) => { const p = parseDraft(t); if (!p.length) return toast.error("Couldn't read the draft format"); setBlocks(p.filter((b) => b.day <= l.days)); toast.info(`${p.length} blocks loaded. Review, then save.`); }} />
        {blocks.map((b, i) => (
          <div key={i} className="grid gap-2 rounded-xl border border-border bg-white p-3 sm:grid-cols-[70px_90px_90px_120px_1fr_auto]" data-testid={`block-row-${i}`}>
            <Input type="number" min={1} max={l.days} value={b.day} onChange={(e) => upd(i, "day", e.target.value)} data-testid={`block-day-${i}`} />
            <Input type="time" value={b.start_time} onChange={(e) => upd(i, "start_time", e.target.value)} data-testid={`block-start-${i}`} />
            <Input type="time" value={b.end_time} onChange={(e) => upd(i, "end_time", e.target.value)} data-testid={`block-end-${i}`} />
            <Select value={b.block_type} onValueChange={(v) => upd(i, "block_type", v)}>
              <SelectTrigger data-testid={`block-type-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Title" value={b.title} onChange={(e) => upd(i, "title", e.target.value)} data-testid={`block-title-${i}`} />
            <Button size="icon" variant="ghost" onClick={() => setBlocks(blocks.filter((_, j) => j !== i))} data-testid={`block-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
            <Input className="sm:col-span-3" placeholder="Location" value={b.location} onChange={(e) => upd(i, "location", e.target.value)} data-testid={`block-location-${i}`} />
            <Input className="sm:col-span-3" placeholder="Curator (optional)" value={b.curator} onChange={(e) => upd(i, "curator", e.target.value)} data-testid={`block-curator-${i}`} />
          </div>
        ))}
        <Button variant="outline" className="rounded-full" onClick={() => setBlocks([...blocks, blank(blocks.at(-1)?.day || 1)])} data-testid="block-add-btn"><Plus className="mr-1 h-4 w-4" />Add block</Button>
        {data.active_bookings > 0 && !pending && (
          <div>
            <p className="text-sm text-muted-foreground">This listing has {data.active_bookings} confirmed booking(s). Changing times, titles or locations is a material change and needs each booking's consent. Guests who decline get a full refund.</p>
            <Textarea className="mt-2" placeholder="Explain the change to guests" value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="change-summary-input" />
          </div>
        )}
        <Button className="rounded-full px-8" onClick={save} disabled={!!pending} data-testid="itinerary-save-btn">{pending ? "Locked until guests respond" : "Save timeline"}</Button>
      </div>
      <div>
        <div className="eyebrow text-muted-foreground">Guest preview</div>
        <div className="mt-4 rounded-xl border border-border bg-white p-5"><ItineraryTimeline blocks={blocks.filter((b) => b.title)} startDate={l.start_date} /></div>
      </div>
    </div>
  );
}
